import { defineStore } from 'pinia';
import { client, LocaleResponse } from '@/api';
import { useUserStore } from '@/store/user';

type LocalesState = {
  loading: boolean;
  error: string | null;
  localeId: number;
  defaultLocaleId: number;
  locales: LocaleResponse[];
  localesWithoutDefLocale: LocaleResponse[];
  mapLocales: Record<string, string>;
};

export const useLocalesStore = defineStore('locales', {
  state: (): LocalesState => ({
    loading: false,
    error: null,
    localeId: -1,
    defaultLocaleId: -1,
    locales: [],
    localesWithoutDefLocale: [],
    mapLocales: {},
  }),
  actions: {
    setLoading(value: boolean) {
      this.loading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setLocaleId(localeId: number) {
      this.localeId = localeId;
    },

    async init() {
      const userStore = useUserStore();

      this.defaultLocaleId = userStore.defaultLocaleId;

      await this.getLocales();
    },

    getLocaleName(localeId: number): string {
      return this.mapLocales[localeId.toString()] ?? 'unknown locale';
    },

    async getLocales(): Promise<LocaleResponse[]> {
      if (this.locales.length > 0 && !this.error) {
        return this.locales;
      }

      if (this.loading) {
        return this.locales;
      }
      this.setError(null);
      this.setLoading(true);

      let data: LocaleResponse[] = [];

      try {
        data = await client.games.getLocales();

        this.mapLocales = data.reduce(
          (acc: Record<string, string>, locale: LocaleResponse) => {
            acc[locale.id] = locale.name;

            return acc;
          },
          {},
        );

        this.locales = data;
        this.localesWithoutDefLocale = data.filter(
          (locale: LocaleResponse) => locale.id !== this.defaultLocaleId,
        );
      } catch (err) {
        this.setError('Error getting locales');
      } finally {
        this.setLoading(false);
      }

      return data;
    },
  },
});
